import { useQuery } from '@apollo/client';
import { useMemo } from 'react';

import { GetOrderQuery } from '../data/queries/getOrderQuery.gql';
import { formatOrder } from '../data/utils/orderUtils';
import type { OrderWithCustomProducts } from '../data/utils/types';

// Type for the query response
type GetOrderQueryResponse = {
  order?: {
    __typename: 'Order';
    [key: string]: any;
  } | null;
};

/**
 * Format the order response from GraphQL query
 *
 * @param data - Raw query data
 * @returns Formatted order or null
 */
const formatOrderResponse = (
  data?: GetOrderQueryResponse
): OrderWithCustomProducts | null => {
  if (!data?.order) return null;

  // Type guard to ensure order is the correct type
  if (data.order.__typename !== 'Order') return null;

  return formatOrder(data.order);
};

/**
 * Hook to fetch a single order by ID
 * Includes order details, PDF URL, and chat information
 *
 * @param orderId - The order ID to fetch
 * @param onError - Optional error handler
 * @returns Order data, loading state, and error
 *
 * @example
 * ```tsx
 * const { order, loading, error } = useGetOrder('order-123');
 *
 * if (loading) return <Spinner />;
 * if (error) return <Error message={error.message} />;
 * if (!order) return <NotFound />;
 *
 * return <OrderDetails order={order} />;
 * ```
 */
export const useGetOrder = (
  orderId: string,
  onError?: (error: Error) => void
) => {
  const { data, loading, error } = useQuery<GetOrderQueryResponse>(
    GetOrderQuery,
    {
      fetchPolicy: 'cache-and-network',
      variables: { id: orderId },
      onError,
      skip: !orderId, // Skip query if no orderId provided
    }
  );

  const order = useMemo(() => formatOrderResponse(data), [data]);

  return {
    order,
    loading,
    error: error || null,
  };
};
